/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/UsersColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Column renderers for Users DataGrid.
────────────────────────────────────────────────────────────────*/

import { useI18n } from '../../../hooks/useI18n';
import { Shield } from 'lucide-react';
import { User } from '../types/users.types';

export function useUsersColumnRenderers() {
  const { t } = useI18n();

  const getColumnRenderer = (key: string) => {
    switch (key) {
      case 'username':
        return (value: string, item: User) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{value}</span>
            {item.isPrimaryAdmin && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                title={t('users.primaryAdmin')}
              >
                <Shield className="h-2.5 w-2.5" />
                {t('users.primaryAdminShort')}
              </span>
            )}
          </div>
        );

      case 'profiles':
        return (value: Array<{ id: string; name: string }>) => (
          <div className="text-sm">
            {value && value.length > 0 ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                {value.length === 1 ? value[0].name : `${value.length} ${t('users.profiles')}`}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('users.none')}</span>
            )}
          </div>
        );

      case 'tenants':
        return (value: Array<{ id: string; name: string }>) => (
          <div className="text-sm">
            {value && value.length > 0 ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                {value.length} {value.length === 1 ? t('users.tenant') : t('users.tenants')}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('users.none')}</span>
            )}
          </div>
        );

      case 'totpEnabled':
        return (value: boolean) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
          >
            {value ? t('users.enabled') : t('users.disabled')}
          </span>
        );

      case 'isActive':
        return (value: boolean) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
          >
            {value ? t('users.enabled') : t('users.disabled')}
          </span>
        );

      case 'created_at':
        return (value: string) => (
          <div className="text-sm text-muted-foreground">
            {new Date(value).toLocaleDateString()}
          </div>
        );

      default:
        return undefined;
    }
  };

  return { getColumnRenderer };
}

